import { createApp, ref, computed, onMounted } from 'vue';
import { Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'vue-awesome-swiper';

// import swiper module styles
import 'swiper/css';
import 'swiper/css/pagination';

/* Components */
import Card from './components/Card.vue';
import HorizontalCard from './components/HorizontalCard.vue';
import SkeletonBox from './components/SkeletonBox.vue';

// Register the component globally
const axios = require('axios');

const sections = document.getElementsByClassName('bookvisit_content_list');

const createAppInstance = (section) => {
  const contentList = window.drupalSettings.bookvisit_content_list;
  const datasetId = contentList[section.dataset.id];
  const app = createApp({
    delimiters: ['${', '}'],
    components: {
      Swiper,
      SwiperSlide,
      Card,
      HorizontalCard,
      SkeletonBox,
    },

    setup() {
      //refs / reactive
      const component = ref(datasetId.itemsPerRow > 1 ? datasetId.card_style : 'HorizontalCard');
      const channel_id = ref(null);
      const error = ref(false);
      const items = ref([]);
      const itemsPerRow = ref(parseInt(datasetId.itemsPerRow || 3));
      const largeCol = ref('col-xl-' + parseInt(datasetId.itemsPerRow || 3));
      const loading = ref(true);
      const type = ref(datasetId.type);
      const style = ref(datasetId.style || 'List');
      const selectedCategories = ref([]);
      const showFilter = ref(datasetId.showFilter || false);
      const accommodationType = ref(datasetId.accommodationType);
      const langId = ref(datasetId.langId || 1); // Default to Swedish
      const sortMethod = ref('rank'); // Set sort order here to be able to change from CMS in future

      //computed properties
      const uniquePackageCategories = computed(() => {
        const packageCategories = items.value
          .map((item) => ({
            name: item.packageCategoryName,
            id: item.packageCategoryId,
          }))
          .reduce((filtered, current) => {
            const existingCategory = filtered.find((item) => item.id === current.id);

            //If current category ID doesn't exist in filtered array, it adds it
            if (!existingCategory) {
              filtered.push(current);
            }

            return filtered;
          }, []);

        return packageCategories;
      });

      const uniqueRoomCategories = computed(() => {
        if (type.value == 'rooms') {
          const roomCategories = items.value
            .map((item) => ({
              name: item.translatedTypeOfAccommodation.text,
              id: item.typeOfAccommodation,
            }))
            .reduce((filtered, current) => {
              const existingRoom = filtered.find((item) => item.id === current.id);

              //If current category ID doesn't exist in filtered array, it adds it
              if (!existingRoom) {
                filtered.push(current);
              }

              return filtered;
            }, []);

          return roomCategories;
        }
        return [];
      });

      const filteredItems = computed(() => {
        // Store temp
        let tempItems = items.value;

        // Sort first - then do filter
        if (sortMethod.value == 'rank') {
          tempItems = tempItems.sort((a, b) => {
            return a.rank - b.rank;
          });
        } else if (sortMethod.value == 'alphabetically') {
          tempItems = tempItems.sort((a, b) => {
            let fa = a.name.toLowerCase(),
              fb = b.name.toLowerCase();
            if (fa < fb) {
              return -1;
            }
            if (fa > fb) {
              return 1;
            }
            return 0;
          });
        }

        if (selectedCategories.value.length === 0) {
          return tempItems; // Return all items if no category is selected
        } else {
          if (type.value == 'packages') {
            return tempItems.filter((item) => selectedCategories.value.includes(item.packageCategoryId));
          } else if (type.value == 'rooms') {
            return tempItems.filter((item) => selectedCategories.value.includes(item.typeOfAccommodation));
          } else {
            return tempItems;
          }
        }
      });

      const componentStyle = computed(() => {
        if (itemsPerRow.value == 1) {
          return (component.value = 'HorizontalCard');
        } else {
          return component.value;
        }
      });

      //methods
      const fetchContent = async () => {
        const tempItems = datasetId.items;
        const apiUrl = `/${window.drupalSettings.path.pathPrefix}api/bookvisit/content/${datasetId.type}`;

        const params = {
          channel_id: datasetId.channel_id,
        };

        try {
          const response = await axios.get(apiUrl, { params });

          if (response.data.code) {
            error.value = true;
          } else if (response.data.data === null) {
            error.value = true;
          } else {
            if (tempItems) {
              items.value = response.data.data.slice(0, tempItems);
            } else {
              items.value = response.data.data;
            }

            channel_id.value = response.data.channel_id;
          }
        } catch (err) {
          error.value = true;
          console.error(err);
        } finally {
          loading.value = false;
        }
      };

      const clearFilter = () => {
        selectedCategories.value = [];
      };

      const roomCheckAccommodationType = () => {
        if (accommodationType.value) {
          selectedCategories.value.push(accommodationType.value);
        }
      };

      //lifecycle hooks
      onMounted(() => {
        fetchContent();

        roomCheckAccommodationType();
      });

      return {
        modules: [Navigation, Pagination],
        component,
        channel_id,
        error,
        items,
        itemsPerRow,
        largeCol,
        loading,
        type,
        style,
        selectedCategories,
        showFilter,
        accommodationType,
        uniquePackageCategories,
        uniqueRoomCategories,
        filteredItems,
        componentStyle,
        fetchContent,
        clearFilter,
        sortMethod,
        langId,
      };
    },
  });

  // app.config.compilerOptions.delimiters = ['${', '}'];

  app.mount('#bookvisit_content_list_' + section.dataset.id);
};

for (let i = 0; i < sections.length; i++) {
  createAppInstance(sections[i]);
}
